import ThemeModal from "components/Common/ThemeModal"
import { Badge, Col, Row, Spinner } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { getEntityLabel, getUserLabel } from "pages/AccountPages/utils/displayValue"

const getMoneyDisplayValue = (value, currency = "BDT") => formatCurrency(value || 0, currency)

const getVehicleLabel = (vehicle, fallbackId) => {
  if (!vehicle) return fallbackId ? `Vehicle #${fallbackId}` : "Not linked"
  const parts = [vehicle.stock_id, vehicle.full_chassis, vehicle.model_year].filter(Boolean)
  return parts.join(" • ") || `Vehicle #${vehicle.id}`
}

const ReceiptRow = ({ label, value, mono = false, strong = false }) => (
  <div className="d-flex align-items-start justify-content-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}>
    <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
    <span className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`.trim()} style={{ fontSize: strong ? 15 : 14 }}>
      {value || "-"}
    </span>
  </div>
)

const ReceiptSection = ({ title, children }) => (
  <div className="pt-3">
    <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>{title}</div>
    {children}
  </div>
)

const AccountVehiclePurchaseView = ({ modal, toggleModal, purchase, loading }) => {
  const importerName = getEntityLabel(
    purchase?.vehicle_importer || purchase?.vehicleImporter,
    purchase?.vehicle_importer_id,
    "Importer"
  )
  const vehicle = purchase?.vehicle
  const vehicleLabel = getVehicleLabel(vehicle, purchase?.vehicle_id)
  const stockId = vehicle?.stock_id || (purchase?.vehicle_id ? `Vehicle #${purchase.vehicle_id}` : "-")
  const chassis = vehicle?.full_chassis || "-"
  const createdBy = getUserLabel(purchase?.createdBy || purchase?.created_by)
  const updatedBy = getUserLabel(purchase?.updatedBy || purchase?.updated_by)
  const totalPaid = purchase?.total_paid
  const due = purchase?.due

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Vehicle Purchase Details" size="xl" modalBodyClass="bg-body-tertiary">
      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : !purchase ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 740 }}>
          <div className="rounded-4 border overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)", borderColor: "rgba(15, 23, 42, 0.08)", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)" }}>

            <div className="px-4 py-2 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)" }}>
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>Vehicle Purchase Record</div>
              <div className="text-white-50 font-monospace" style={{ fontSize: 12 }}>AVP-{String(purchase.id ?? "0000").padStart(4, "0")}</div>
            </div>

            <div className="px-4 py-3 border-bottom" style={{ background: "radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)", borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Importer</div>
                  <div className="h5 mb-1 text-dark" style={{ letterSpacing: -0.2 }}>{importerName}</div>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge color="warning" pill className="px-2 py-1">Purchased</Badge>
                    <span className="text-muted small font-monospace">#{purchase.id ?? "-"}</span>
                    <span className="text-muted small">{purchase.purchase_date ? formatReadableDate(purchase.purchase_date) : "No date"}</span>
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Purchase Price</div>
                  <div className="fw-bold text-dark" style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}>{getMoneyDisplayValue(purchase.purchase_price)}</div>
                  <div className="text-muted small mt-1">from {importerName}</div>
                </div>
              </div>
            </div>

            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed rgba(15, 23, 42, 0.18)" }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, left: -11, top: -2 }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, right: -11, top: -2 }} />
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Purchase Details">
                    <ReceiptRow label="Importer" value={importerName} />
                    <ReceiptRow label="Source" value={purchase.source_type === "bank_lc" ? "Bank LC (Import)" : "Importer"} />
                    {purchase.source_type === "bank_lc" && (
                      <>
                        <ReceiptRow label="LC Number" value={purchase.lc_number} mono />
                        <ReceiptRow label="LC Bank Account" value={getEntityLabel(purchase.bank_account || purchase.bankAccount, purchase.bank_account_id, "Bank Account")} />
                      </>
                    )}
                    <ReceiptRow label="Purchase Price" value={getMoneyDisplayValue(purchase.purchase_price)} mono />
                    <ReceiptRow label="Purchase Date" value={purchase.purchase_date ? formatReadableDate(purchase.purchase_date) : "-"} />
                    <ReceiptRow label="Record ID" value={purchase.id ?? "-"} mono />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Vehicle Info">
                    <ReceiptRow label="Vehicle" value={vehicleLabel} />
                    <ReceiptRow label="Stock ID" value={stockId} mono />
                    <ReceiptRow label="Chassis" value={chassis} mono />
                    <ReceiptRow label="Vehicle ID" value={vehicle?.id ?? "-"} mono />
                  </ReceiptSection>
                </Col>
              </Row>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Payment Summary">
                <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  <ReceiptRow label="Purchase Price" value={getMoneyDisplayValue(purchase.purchase_price)} mono />
                  <ReceiptRow label="Total Paid to Importer" value={totalPaid !== undefined ? getMoneyDisplayValue(totalPaid) : "-"} mono />
                  <ReceiptRow label="Amount Due" value={due !== undefined ? getMoneyDisplayValue(due) : "-"} strong mono />
                </div>
              </ReceiptSection>
            </div>

            {purchase.notes && (
              <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
                <ReceiptSection title="Notes">
                  <div className="text-dark rounded-3 px-3 py-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, background: "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                    {purchase.notes}
                  </div>
                </ReceiptSection>
              </div>
            )}

            <div className="px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)" }}>
              <Row className="g-3">
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Created By</div>
                  <div className="small text-dark">{createdBy}</div>
                  <div className="small text-muted">{purchase.created_at ? formatReadableDate(purchase.created_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Updated By</div>
                  <div className="small text-dark">{updatedBy}</div>
                  <div className="small text-muted">{purchase.updated_at ? formatReadableDate(purchase.updated_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Record Status</div>
                  <div className="small text-dark fw-semibold">{purchase.deleted_at ? "Archived" : "Active"}</div>
                  <div className="small text-muted">{purchase.deleted_at ? formatReadableDate(purchase.deleted_at) : "No deletion recorded"}</div>
                </Col>
              </Row>
            </div>

          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountVehiclePurchaseView
